/**
 * GalleryImageReplyPreview Component
 * Shows gallery image preview above message composer when replying to a gallery image
 * Clean design with proper aspect ratio preservation
 */

export interface GalleryImagePreview {
  id: string
  title: string | null
  thumbnail_url: string
  original_width: number
  original_height: number
}

interface GalleryImageReplyPreviewProps {
  image: GalleryImagePreview
  onCancel: () => void
}

export function GalleryImageReplyPreview({ image, onCancel }: GalleryImageReplyPreviewProps) {
  // Calculate aspect ratio for proper image display
  const aspectRatio = image.original_width / image.original_height
  const isLandscape = aspectRatio > 1
  const isPortrait = aspectRatio < 1

  // Max dimensions for the preview (bigger for better visibility, especially mobile)
  const maxHeight = 120 // px
  const maxWidth = 160 // px

  // Calculate dimensions preserving aspect ratio
  let width: number
  let height: number

  if (isLandscape) {
    width = Math.min(maxWidth, maxHeight * aspectRatio)
    height = width / aspectRatio
  } else if (isPortrait) {
    height = maxHeight
    width = height * aspectRatio
  } else {
    // Square
    width = maxHeight
    height = maxHeight
  }

  return (
    <div className="flex items-center gap-2 px-3 py-2 bg-purple-50 border-l-4 border-braydin-olive animate-slide-in-up">
      {/* Spacer pushes everything to the right */}
      <div className="flex-1" />

      {/* Label next to image */}
      <div className="text-sm font-medium text-braydin-oliveDark whitespace-nowrap">
        Replying to gallery:
      </div>

      {/* Image thumbnail - bigger and on the right */}
      <div
        className="flex-shrink-0 rounded-lg overflow-hidden bg-gray-200 shadow-sm"
        style={{ width: `${width}px`, height: `${height}px` }}
      >
        <img
          src={image.thumbnail_url}
          alt="Gallery image"
          className="w-full h-full object-cover"
        />
      </div>

      {/* Cancel button - far right */}
      <button
        onClick={onCancel}
        className="flex-shrink-0 w-7 h-7 flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-braydin-sand2 rounded-full transition-colors"
        aria-label="Cancel reply"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  )
}
